import { Button, Spin, Table } from "antd";
import React, { useEffect, useState } from "react";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { adminContext } from "../../Common/PageRoute";
import AddPractice from "./AddPractices";
import DailyPracticeStats from "../../Services/DailyPracticeStats";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import CleanCatchService from "../../Services/CleanCatchService";
import { useUser } from "../../Context/UserContext";
import {
    PageLayout,
    PageHeader,
    SectionCard,
    TableToolbar,
    EmptyState,
    FilterCriteria,
} from "../../Common/Layout";
import { useResizableColumns } from "../../Common/Custom Components/Ant design/useResizableColumns";

interface ListDataType {
    key: React.Key;
    id: number;
    name: string;
    phoneNumber: string;
    email: string;
    createdBy: any;
    updatedBy: any;
    inActive?: boolean;
    practices: any[];
}
interface ListData {
    items: ListDataType[];
    totalItems: number;
    practices: any[];
}

const DEFAULT_SORT = {
    sortCol: "practiceId",
    sortDir: "ascend" as "ascend" | "descend",
};

const PracticesTable = () => {
    const { openNotification } = React.useContext(adminContext);
    const { isSubAdmin } = useUser();
    const isViewOnly = isSubAdmin();
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [dialog, setDialog] = useState<any>({ type: "", params: {} });
    const [dailyPracticeStatsData, setDailyPracticeStatsData] = useState<any>([]);

    // Single shared sort state â€” no per-tab sorting
    const [currentSort, setCurrentSort] = useState<{
        sortCol: string;
        sortDir: "ascend" | "descend";
    }>(DEFAULT_SORT);

    const [pagingItems, setPagingItems] = React.useState({
        totalRecords: 0,
        currentPage: 1,
        pageSize: 15,
    });

    const [statsFilters, setStatsFilters] = useState<{
        search: string;
        status: string;
        fromDate: Dayjs | null;
        toDate: Dayjs | null;
    }>({
        search: "",
        status: "",
        fromDate: dayjs().startOf("month"),
        toDate: dayjs(),
    });

    const fetchStatsData = async (
        pageSize?: number,
        currentPage?: number,
        sortCol?: string,
        sortDir?: string,
    ) => {
        setLoading(true);
        try {
            const page = currentPage ?? pagingItems.currentPage;
            const size = pageSize ?? pagingItems.pageSize;
            const start = (page - 1) * size;
            const col = sortCol ?? currentSort.sortCol;
            const dir = sortDir ?? currentSort.sortDir;

            const params: any = {
                start,
                length: size,
                sortCol: col,
                sortDir: dir,
            };

            if (statsFilters.search) params.search = statsFilters.search;
            if (statsFilters.fromDate) {
                params.fromDate = statsFilters.fromDate.startOf("day").toISOString();
            }
            if (statsFilters.toDate) {
                params.toDate = statsFilters.toDate.endOf("day").toISOString();
            }

            const res = await DailyPracticeStats.getDailyPracticeStats(params);
            setDailyPracticeStatsData(res || []);

            if (res?.result?.totalRecords) {
                setPagingItems((prev) => ({
                    ...prev,
                    totalRecords: res.result?.totalRecords,
                }));
            }
        } catch (error) {
            console.error(error);
            openNotification("error", "Failed to load statistics");
        } finally {
            setLoading(false);
        }
    };

    // Re-fetch on search / date filter change â€” reset to page 1
    useEffect(() => {
        setPagingItems((prev) => ({ ...prev, currentPage: 1 }));
        fetchStatsData(pagingItems.pageSize, 1, currentSort.sortCol, currentSort.sortDir);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statsFilters.search, statsFilters.fromDate, statsFilters.toDate, refresh]);

    const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
        let sortCol = DEFAULT_SORT.sortCol;
        let sortDir: "ascend" | "descend" = "ascend";

        if (sorter?.order) {
            sortDir = sorter.order === "descend" ? "descend" : "ascend";
        }

        if (sorter?.field) {
            let field = sorter.field;
            if (Array.isArray(field)) field = field.join(",");
            if (typeof field === "string" && field.includes(",")) {
                sortCol = field.split(",")[1];
            } else {
                sortCol = field;
            }
        }

        const newPage = pagination.current;
        const newPageSize = pagination.pageSize;

        setPagingItems({
            currentPage: newPage,
            pageSize: newPageSize,
            totalRecords: pagination.total,
        });

        setCurrentSort({ sortCol, sortDir });
        fetchStatsData(newPageSize, newPage, sortCol, sortDir);
    };

   
    const openAddPractice = () => {
        setDialog({ type: "addPractice", params: {} });
    };

    const rawColumns: any[] = [
        {
            title: "S.No",
            key: "sno",
            width: 50,
            toggleable: false,
            fixed: "left",
            render: (_: any, __: any, index: number) =>
                (pagingItems.currentPage - 1) * pagingItems.pageSize + index + 1,
        },
        {
            title: "Tenant Id",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "practiceId",
            key: "practiceId",
            fixed: "left",
            width: 130,
            sortOrder:
                currentSort.sortCol === "practiceId" ? currentSort.sortDir : undefined,
            toggleable: false,
            render: (id: number) => `Tenant - ${id}`,
        },
        {
            title: "Name",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "practiceName",
            key: "practiceName",
            width: 250,
            fixed: "left",
            sortOrder:
                currentSort.sortCol === "practiceName" ? currentSort.sortDir : undefined,
            toggleable: false,
            render: (text: string, record: any) => (
                <Link
                    to={`/practices/${record.practiceId}`}
                    state={{ practiceName: record.practiceName }}
                >
                    {text}
                </Link>
            ),
        },
        {
            title: "AML",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "aml",
            key: "aml",
            width: 80,
            group: "Expenses",
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.aml === null || record?.stats?.aml === 0 ? "-" : record?.stats?.aml}`,
        },
        {
            title: "AML-Biometric",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "amL_Biometric",
            key: "amL_Biometric",
            width: 130,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.amL_Biometric === null || record?.stats?.amL_Biometric === 0 ? "-" : record?.stats?.amL_Biometric}`,
        },
        {
            title: "Incoming",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "incoming",
            key: "incoming",
            width: 100,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.incoming === null || record?.stats?.incoming === 0 ? "-" : record?.stats?.incoming}`,
        },
        {
            title: "Outgoing",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "outgoing",
            key: "outgoing",
            width: 100,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.outgoing === null || record?.stats?.outgoing === 0 ? "-" : record?.stats?.outgoing}`,
        },
        {
            title: "Emails",
            sorter: true,
            showSorterTooltip: true,
            dataIndex: "emails",
            key: "emails",
            width: 100,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.emails === null || record?.stats?.emails === 0 ? "-" : record?.stats?.emails}`,
        },
        {
            title: "Storage Uses",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "storageUses",
            key: "storageUses",
            width: 120,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.storageUses === null || record?.stats?.storageUses === 0 ? "-" : record?.stats?.storageUses}`,
        },
        {
            title: "CS",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "cs",
            key: "cs",
            width: 70,
            group: "Uses",
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.cs === null || record?.stats?.cs === 0 ? "-" : record?.stats?.cs}`,
        },
        {
            title: "CF",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "cf",
            key: "cf",
            width: 70,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.cf === null || record?.stats?.cf === 0 ? "-" : record?.stats?.cf}`,
        },
        {
            title: "Accounts",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "accountsReport",
            key: "accountsReport",
            width: 90,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.accountsReport === null || record?.stats?.accountsReport === 0 ? "-" : record?.stats?.accountsReport}`,
        },
        {
            title: "CT",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "ct",
            key: "ct",
            width: 70,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.ct === null || record?.stats?.ct === 0 ? "-" : record?.stats?.ct}`,
        },
        {
            title: "VAT",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "vat",
            key: "vat",
            width: 70,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.vat === null || record?.stats?.vat === 0 ? "-" : record?.stats?.vat}`,
        },
        {
            title: "EPS",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "eps",
            key: "eps",
            width: 70,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.eps === null || record?.stats?.eps === 0 ? "-" : record?.stats?.eps}`,
        },
        {
            title: "FPS",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "fps",
            key: "fps",
            width: 70,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.fps === null || record?.stats?.fps === 0 ? "-" : record?.stats?.fps}`,
        },
        {
            title: "CIS",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "cis",
            key: "cis",
            width: 70,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.cis === null || record?.stats?.cis === 0 ? "-" : record?.stats?.cis}`,
        },
        {
            title: "SA100",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "sA100",
            key: "sA100",
            width: 80,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.sA100 === null || record?.stats?.sA100 === 0 ? "-" : record?.stats?.sA100}`,
        },
        {
            title: "SA800",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "sA800",
            key: "sA800",
            width: 80,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.sA800 === null || record?.stats?.sA800 === 0 ? "-" : record?.stats?.sA800}`,
        },
        {
            title: "SA900",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "sA900",
            key: "sA900",
            width: 80,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.sA900 === null || record?.stats?.sA900 === 0 ? "-" : record?.stats?.sA900}`,
        },
        {
            title: "Active Clients",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "activeClients",
            key: "activeClients",
            width: 110,
            group: "Financial",
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.activeClients === null || record?.stats?.activeClients === 0 ? "-" : record?.stats?.activeClients}`,
        },
        {
            title: "Stripe Count",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "stripeCount",
            key: "stripeCount",
            width: 110,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.stripeCount === null || record?.stats?.stripeCount === 0 ? "-" : record?.stats?.stripeCount}`,
        },
        {
            title: "Stripe Amt.",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "stripeTotalAmount",
            key: "stripeTotalAmount",
            width: 150,
            align: "right",
            render: (_: string, record: any) =>
                record?.stats?.stripeTotalAmount == null ||
                    record?.stats?.stripeTotalAmount === 0
                    ? "-"
                    : `£${Number(record.stats.stripeTotalAmount).toLocaleString("en-GB", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}`,
        },
        {
            title: "GoCardless Count",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "goCardlessCount",
            key: "goCardlessCount",
            width: 140,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.goCardlessCount === null || record?.stats?.goCardlessCount === 0 ? "-" : record?.stats?.goCardlessCount}`,
        },
        {
            title: "GoCardless Amt.",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "goCardlessTotalAmount",
            key: "goCardlessTotalAmount",
            width: 150,
            align: "right",
            render: (_: string, record: any) =>
                record?.stats?.goCardlessTotalAmount == null ||
                    record?.stats?.goCardlessTotalAmount === 0
                    ? "-"
                    : `£${Number(record.stats.goCardlessTotalAmount).toLocaleString("en-GB", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}`,
        },
        {
            title: "Invoices Count",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "invoicesCount",
            key: "invoicesCount",
            width: 120,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.invoicesCount === null || record?.stats?.invoicesCount === 0 ? "-" : record?.stats?.invoicesCount}`,
        },
        {
            title: "Invoices Amt.",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "invoicesAmount",
            key: "invoicesAmount",
            width: 150,
            align: "right",
            render: (_: string, record: any) =>
                record?.stats?.invoicesAmount == null ||
                    record?.stats?.invoicesAmount === 0
                    ? "-"
                    : `£${Number(record.stats.invoicesAmount).toLocaleString("en-GB", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}`,
        },
        {
            title: "Receipts Count",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "receiptsCount",
            key: "receiptsCount",
            width: 120,
            align: "center",
            render: (text: string, record: any) =>
                `${record?.stats?.receiptsCount === null || record?.stats?.receiptsCount === 0 ? "-" : record?.stats?.receiptsCount}`,
        },
        {
            title: "Receipts Amt.",
            sorter: true,
            showSorterTooltip: false,
            dataIndex: "receiptsAmount",
            key: "receiptsAmount",
            width: 150,
            align: "right",
            render: (_: string, record: any) =>
                record?.stats?.receiptsAmount == null ||
                    record?.stats?.receiptsAmount === 0
                    ? "-"
                    : `£${Number(record.stats.receiptsAmount).toLocaleString("en-GB", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}`,
        },
    ];

    const { columns, components, columnSelector } = useResizableColumns({
        columns: rawColumns,
        storageKey: "ao_practices_dashboard"
    });


    // const getColumnsForCategory = (category: CategoryKey): ColumnsType<any> => {
    //     const { sortCol, sortDir } = currentSort;

    //     // All uncommon column keys for this tab
    //     const thisTabColumnKeys = categories[category].map((c) => c.key);

    //     const categoryColumns = categories[category]
    //         .filter((col) => !["date", "createdAt", "updatedAt"].includes(col.key))
    //         .map((col) => {
    //             const isAmountCol = [
    //                 "invoicesAmount",
    //                 "receiptsAmount",
    //                 "goCardlessTotalAmount",
    //                 "stripeTotalAmount",
    //             ].includes(col.key);

    //             const column: any = {
    //                 title: col.title,
    //                 sorter: true,
    //                 showSorterTooltip: false,
    //                 dataIndex: ["stats", col.key],
    //                 key: col.key,
    //                 width: col.width,
    //                 align: isAmountCol ? "right" : "center",
    //                 // Show sortOrder ONLY if:
    //                 // - This column is the sorted column AND
    //                 // - The sorted column belongs to THIS tab (not another tab's column)
    //                 // If a different tab's uncommon column is sorted â†’ show no indicator here
    //                 sortOrder:
    //                     col.key === sortCol && thisTabColumnKeys.includes(sortCol)
    //                         ? sortDir
    //                         : undefined,
    //                 render: isAmountCol
    //                     ? (value: any) => {
    //                         if (value == null || value === 0) return "-";
    //                         return `Â£${Number(value).toLocaleString("en-GB", {
    //                             minimumFractionDigits: 2,
    //                             maximumFractionDigits: 2,
    //                         })}`;
    //                     }
    //                     : (value: any) => (value == null || value === 0 ? "-" : value),
    //             };

    //             return column;
    //         });

    //     return [...commonColumns, ...categoryColumns];
    // };

    const primaryActions = [];
    if (!isViewOnly) {
        primaryActions.push({
            key: "add",
            label: "Add Practice",
            icon: <PlusOutlined />,
            type: "primary" as const,
            onClick: openAddPractice,
        });
        primaryActions.push({
            key: "cache",
            label: "Clean Cache",
            type: "danger" as const,
            onClick: () => setDialog({ type: "addCleanCache", params: {} }),
        });
    }

    const filterCriteria: FilterCriteria[] = [
        { key: "dateRange", label: "Date Range", type: "dateRange" },
    ];

    const activeFilters = {
        dateRange:
            statsFilters.fromDate && statsFilters.toDate
                ? [statsFilters.fromDate, statsFilters.toDate]
                : undefined,
    };

    const handleFiltersChange = (newFilters: Record<string, any>) => {
        const dates = newFilters.dateRange || [null, null];
        setStatsFilters((prev) => ({ ...prev, fromDate: dates[0], toDate: dates[1] }));
    };

    return (
        <>
            <PageLayout>
                <PageHeader
                    title="Practices"
                    subtitle="Daily statistics and activity across all practice tenants"
                    actions={
                        primaryActions.map((a) => (
                            <Button
                                key={a.key}
                                type={a.type === "primary" ? "primary" : "default"}
                                danger={a.type === "danger"}
                                icon={a.icon}
                                onClick={a.onClick}
                            >
                                {a.label}
                            </Button>
                        ))
                    }
                />

                <SectionCard noPadding>
                    <div style={{ padding: "0 16px" }}>
                        <TableToolbar
                            onSearch={(val) => setStatsFilters((prev) => ({ ...prev, search: val }))}
                            searchPlaceholder="Search practices..."
                            filterCriteria={filterCriteria}
                            activeFilters={activeFilters}
                            onFiltersChange={handleFiltersChange}
                            onRefresh={() => setRefresh(!refresh)}
                            extra={columnSelector}
                        />
                    </div>

                    <Table
                        dataSource={dailyPracticeStatsData?.items || []}
                        columns={columns}
                        components={components}
                        size="small"
                        tableLayout="fixed"
                        loading={{ spinning: loading, indicator: <Spin indicator={<LoadingOutlined />} /> }}
                        className="border-top-table"
                        // scroll={{
                        //     x: "max-content",
                        //     ...((dailyPracticeStatsData?.items?.length || 0) > 15 ? { y: "calc(100vh - 340px)" } : {}),
                        // }}
                        scroll={{
                            x: "max-content",
                            y: (dailyPracticeStatsData?.length || 0) > 10 ? window.innerHeight - 256 : undefined
                        }}
                        locale={{
                            emptyText: (
                                <EmptyState
                                    title="No practices found"
                                    description="Try adjusting your search or date filter"
                                />
                            ),
                        }}
                        pagination={{
                            current: pagingItems.currentPage,
                            pageSize: pagingItems.pageSize,
                            total: dailyPracticeStatsData?.totalRecords || 0,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            showSizeChanger: true,
                            pageSizeOptions: ["15", "25", "50", "100"],
                        }}
                        onChange={handleTableChange}
                    />

                </SectionCard>
            </PageLayout>

            {dialog.type === "addPractice" && (
                <AddPractice onDismiss={() => setDialog({ type: "", params: {} })} />
            )}

            {dialog.type === "addCleanCache" && (
                <ConfirmationModal
                    open={true}
                    onNo={() => setDialog({ type: "", params: "" })}
                    onYes={async () => {
                        try {
                            const apiData = await CleanCatchService.postCleanCache();
                            if (apiData.result) {
                                openNotification("success", "Clean cache sucessfully.");
                                setDialog({ type: "", params: "" });
                            } else {
                                openNotification("error", apiData?.message ?? "Something went wrong");
                                setDialog({ type: "", params: "" });
                            }
                        } catch (err) {
                            openNotification("error", err);
                            setDialog({ type: "", params: "" });
                        }
                    }}
                    text="Are you sure you want to clean the cache?"
                />
            )}
        </>
    );
};

export default PracticesTable;
